import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { marketplaceInquiryStatusLabel, type MarketplaceProduct, type MarketplaceShop } from '@isbd/shared';
import { kuunda } from '@/lib/kuunda';
import { normalizeProduct } from '@/features/marketplace/normalize';
import { AdminEmpty, AdminPageHeader } from '@/features/admin/ui';
import { ListSkeleton } from '@/features/ui/skeleton';
import { useToast } from '@/features/ui/toast/useToast';
import '@/features/admin/admin.css';

type InquiryRow = {
  id: string;
  visitor_name: string;
  visitor_email: string;
  message: string;
  status: string;
  created_at: string;
  product_id: string;
};

const INQUIRY_STATUSES = ['new', 'in_progress', 'closed'] as const;

export function AdminMarketplacePage() {
  const toast = useToast();
  const [shops, setShops] = useState<MarketplaceShop[]>([]);
  const [products, setProducts] = useState<MarketplaceProduct[]>([]);
  const [inquiries, setInquiries] = useState<InquiryRow[]>([]);
  const [leadRows, setLeadRows] = useState<{ user_id: string; status: string }[]>([]);
  const [selectedShopId, setSelectedShopId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const [sRes, pRes, iRes, leadsRes] = await Promise.all([
      kuunda.from('marketplace_shops').select('*').order('created_at', { ascending: false }),
      kuunda.from('marketplace_products').select('*').order('created_at', { ascending: false }).limit(200),
      kuunda.from('marketplace_inquiries').select('*').order('created_at', { ascending: false }).limit(100),
      kuunda
        .from('crm_leads')
        .select('id, user_id, source, status')
        .eq('source', 'marketplace')
        .limit(500),
    ]);
    if (sRes.error) toast.error(sRes.error.message);
    else {
      const nextShops = (sRes.data ?? []) as MarketplaceShop[];
      setShops(nextShops);
      setSelectedShopId((prev) => prev ?? nextShops[0]?.id ?? null);
    }
    if (pRes.error) toast.error(pRes.error.message);
    else setProducts((pRes.data ?? []).map((row) => normalizeProduct(row as Record<string, unknown>)));
    if (iRes.error) toast.error(iRes.error.message);
    else {
      setInquiries(
        (iRes.data ?? []).map((row) => ({
          id: String(row.id),
          visitor_name: String(row.visitor_name ?? ''),
          visitor_email: String(row.visitor_email ?? ''),
          message: String(row.message ?? ''),
          status: String(row.status ?? 'new'),
          created_at: String(row.created_at ?? ''),
          product_id: String(row.product_id ?? ''),
        })),
      );
    }
    if (leadsRes.error) toast.error(leadsRes.error.message);
    else {
      setLeadRows(
        (leadsRes.data ?? []).map((row) => ({
          user_id: String(row.user_id ?? ''),
          status: String(row.status ?? ''),
        })),
      );
    }
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, []);

  async function togglePremium(product: MarketplaceProduct) {
    const { error: err } = await kuunda
      .from('marketplace_products')
      .update({ is_premium: !product.is_premium })
      .eq('id', product.id);
    if (err) toast.error(err.message);
    else await load();
  }

  async function toggleFeatured(product: MarketplaceProduct) {
    const { error: err } = await kuunda
      .from('marketplace_products')
      .update({ is_featured: !product.is_featured })
      .eq('id', product.id);
    if (err) toast.error(err.message);
    else await load();
  }

  async function togglePublished(product: MarketplaceProduct) {
    const { error: err } = await kuunda
      .from('marketplace_products')
      .update({ is_published: !product.is_published })
      .eq('id', product.id);
    if (err) toast.error(err.message);
    else await load();
  }

  async function removeProduct(id: string) {
    if (!window.confirm('Supprimer définitivement ce produit ?')) return;
    const { error: err } = await kuunda.from('marketplace_products').delete().eq('id', id);
    if (err) toast.error(err.message);
    else await load();
  }

  async function setInquiryStatus(id: string, status: string) {
    const { error: err } = await kuunda.from('marketplace_inquiries').update({ status }).eq('id', id);
    if (err) {
      toast.error(err.message);
    } else {
      toast.success('Statut de la demande mis à jour.', 'Marketplace');
      await load();
    }
  }

  const performanceByShopId = Object.fromEntries(
    shops.map((shop) => {
      const shopProducts = products.filter((product) => product.shop_id === shop.id);
      const productIds = new Set(shopProducts.map((product) => product.id));
      const shopInquiries = inquiries.filter((inq) => productIds.has(inq.product_id));
      return [
        shop.id,
        {
          requests: shopInquiries.length,
          gained: leadRows.filter((lead) => lead.user_id === shop.user_id && lead.status === 'gagne').length,
          lost: leadRows.filter((lead) => lead.user_id === shop.user_id && lead.status === 'perdu').length,
        },
      ];
    }),
  ) as Record<string, { requests: number; gained: number; lost: number }>;

  const selectedShop = shops.find((shop) => shop.id === selectedShopId) ?? null;
  const selectedShopProducts = selectedShop ? products.filter((product) => product.shop_id === selectedShop.id) : [];

  return (
    <div className="admin-page">
      <AdminPageHeader
        title="Marketplace"
        description="Modération des produits et suivi des expressions de besoin."
        actions={
          <Link to="/marketplace" className="btn btn-ghost btn-sm" target="_blank" rel="noreferrer">
            Boutique publique
          </Link>
        }
      />

      <section className="admin-panel">
        <div className="admin-panel-title">
          <h3>Boutiques ({shops.length})</h3>
        </div>
        {loading ? <ListSkeleton rows={4} /> : null}
        {!loading && shops.length === 0 ? (
          <AdminEmpty icon="store" title="Aucune boutique" hint="Les boutiques marketplace apparaîtront ici." />
        ) : (
          <div style={{ display: 'grid', gap: '0.65rem' }}>
            {shops.map((shop) => {
              const perf = performanceByShopId[shop.id] ?? { requests: 0, gained: 0, lost: 0 };
              return (
                <article
                  key={shop.id}
                  className="admin-list-item"
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.6rem',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: selectedShopId === shop.id ? 'rgba(74, 126, 176, 0.08)' : undefined,
                  }}
                >
                  <div>
                    <strong>{shop.name}</strong>
                    <div className="muted" style={{ fontSize: '0.85rem' }}>
                      {shop.description || 'Sans description'}
                    </div>
                    <div className="muted" style={{ fontSize: '0.85rem' }}>
                      Demandes: {perf.requests} · Clients gagnés: {perf.gained} · Clients perdus: {perf.lost}
                    </div>
                  </div>
                  <div className="admin-row-actions">
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => setSelectedShopId(shop.id)}>
                      Ouvrir
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <section className="admin-panel">
        <div className="admin-panel-title">
          <h3>Produits de la boutique</h3>
        </div>
        {!selectedShop ? (
          <p className="muted">Sélectionnez une boutique pour voir et gérer ses produits.</p>
        ) : (
          <div style={{ display: 'grid', gap: '0.65rem' }}>
            {selectedShopProducts.map((p) => (
              <article key={p.id} className="admin-list-item">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>{p.name}</strong>
                    <div className="muted" style={{ fontSize: '0.85rem' }}>
                      {p.seller_name} · {p.category} · {p.is_published ? 'Publié' : 'Masqué'}
                      {p.is_premium ? ' · Premium' : ''}
                      {p.is_featured ? ' · Vedette' : ''}
                    </div>
                  </div>
                  <div className="admin-row-actions">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => void toggleFeatured(p)}>
                      {p.is_featured ? 'Retirer vedette' : 'Vedette'}
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => void togglePremium(p)}>
                      {p.is_premium ? 'Retirer premium' : 'Premium'}
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => void togglePublished(p)}>
                      {p.is_published ? 'Dépublier' : 'Publier'}
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => void removeProduct(p.id)}>
                      Supprimer
                    </button>
                  </div>
                </div>
              </article>
            ))}
            {selectedShopProducts.length === 0 ? <p className="muted">Aucun produit dans cette boutique.</p> : null}
          </div>
        )}
      </section>

      <section className="admin-panel">
        <div className="admin-panel-title">
          <h3>Demandes ({inquiries.length})</h3>
        </div>
        {!loading && inquiries.length === 0 ? (
          <AdminEmpty icon="inbox" title="Aucune demande" hint="Les contacts visiteurs apparaîtront ici." />
        ) : (
          <div style={{ display: 'grid', gap: '0.65rem' }}>
            {inquiries.map((inq) => (
              <article key={inq.id} className="admin-list-item">
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '0.5rem' }}>
                  <strong>{inq.visitor_name}</strong>
                  <span className={`chip ${inq.status === 'closed' ? 'ok' : 'warn'}`}>
                    {marketplaceInquiryStatusLabel(inq.status)}
                  </span>
                </div>
                <div className="muted" style={{ fontSize: '0.85rem' }}>
                  {inq.visitor_email}
                  {inq.created_at ? ` · ${new Date(inq.created_at).toLocaleString('fr-FR')}` : ''}
                  {inq.product_id ? (
                    <>
                      {' · '}
                      <Link to={`/marketplace/${inq.product_id}`} target="_blank" rel="noreferrer">
                        Voir le produit
                      </Link>
                    </>
                  ) : null}
                </div>
                <p style={{ margin: '0.5rem 0', whiteSpace: 'pre-wrap' }}>{inq.message}</p>
                <div className="admin-row-actions">
                  {INQUIRY_STATUSES.map((status) => (
                    <button
                      key={status}
                      type="button"
                      className={`btn btn-sm ${inq.status === status ? 'btn-primary' : 'btn-ghost'}`}
                      disabled={inq.status === status}
                      onClick={() => void setInquiryStatus(inq.id, status)}
                    >
                      {marketplaceInquiryStatusLabel(status)}
                    </button>
                  ))}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
